import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { MessageSquarePlus, CheckCircle2, Loader2 } from "lucide-react";

const CATEGORIES = [
  { value: "bug",             label: "Bug" },
  { value: "feature_request", label: "Feature Request" },
  { value: "general",         label: "General Feedback" },
];
const MAX = 5000;

export default function FeedbackModal({ open, onOpenChange }) {
  const loc = useLocation();
  const [category, setCategory] = useState("bug");
  const [message, setMessage] = useState("");
  const [contact, setContact] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const reset = () => { setCategory("bug"); setMessage(""); setContact(false); setError(""); setDone(false); };

  const submit = async () => {
    setError("");
    const trimmed = message.trim();
    if (!trimmed) { setError("Please add a message before sending."); return; }
    if (trimmed.length > MAX) { setError(`Message is too long (max ${MAX} characters).`); return; }
    setBusy(true);
    try {
      await api.post("/feedback", {
        category, message: trimmed,
        page: `${loc.pathname}${loc.search || ""}`.slice(0, 256),
        contact_allowed: contact,
      });
      setDone(true);
      setTimeout(() => { onOpenChange(false); reset(); }, 1400);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Couldn't send feedback. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent data-testid="feedback-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageSquarePlus className="h-4 w-4" /> Send feedback</DialogTitle>
          <DialogDescription>
            Tell the DocCircle team what's working, what's broken, or what you'd love to see next.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-6 flex flex-col items-center text-center gap-2" data-testid="feedback-success">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
            <p className="text-sm font-medium">Thanks — your feedback was sent.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Category</label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setCategory(c.value)}
                    data-testid={`feedback-cat-${c.value}`}
                    className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                      category === c.value
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border text-muted-foreground hover:bg-secondary"
                    }`}
                    aria-pressed={category === c.value}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Message</label>
              <Textarea
                data-testid="feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MAX))}
                placeholder={category === "bug"
                  ? "What went wrong? Steps to reproduce help us the most."
                  : category === "feature_request"
                  ? "What would you love DocCircle to do?"
                  : "Tell us what's on your mind."}
                rows={5}
                maxLength={MAX}
              />
              <div className="text-[11px] text-muted-foreground mt-1 flex justify-between">
                <span>Current page: <span className="font-mono">{loc.pathname}</span></span>
                <span>{message.length}/{MAX}</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="feedback-contact"
                data-testid="feedback-contact"
                checked={contact}
                onCheckedChange={setContact}
              />
              <label htmlFor="feedback-contact" className="text-sm text-muted-foreground">
                You may contact me about this feedback
              </label>
            </div>

            {error && <div className="text-sm text-red-600" data-testid="feedback-error">{error}</div>}
          </div>
        )}

        {!done && (
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="feedback-cancel">Cancel</Button>
            <Button onClick={submit} disabled={busy || !message.trim()} data-testid="feedback-submit">
              {busy ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Sending…</> : "Send feedback"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
